import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import { useAuth } from "../auth/AuthContext";
import {
  api,
  API_BASE_URL,
  ApiError,
  type PlayerDashboardPayload,
  type PlayerDashboardTournamentCard,
} from "../lib/api";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";

function registrationFileUrl(path: string | null | undefined) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = API_BASE_URL.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${appBase}${normalized}`;
}

function playerDocumentPath(src: string, label: string, back: string) {
  const params = new URLSearchParams({ src, label, back });
  return `/player/documento?${params.toString()}`;
}

function findTournament(data: PlayerDashboardPayload | null, tournamentId: number): PlayerDashboardTournamentCard | null {
  return data?.tournaments.find((item) => item.tournament_id === tournamentId) ?? null;
}

export default function PlayerTournamentCredentialPage() {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState<PlayerDashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cuenta familiar: la credencial es la del perfil elegido para ESTE torneo
  // (bug de Eduardo 2026-09-29: salía la de la hermana).
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;

  useEffect(() => {
    if (!token || !Number.isFinite(tournamentId) || !scope.ready) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.dashboards.player(token, scopeProfileId, { tournamentId })
      .then((response) => {
        if (!cancelled) setDashboard(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [scope.ready, scopeProfileId, token, tournamentId]);

  const card = useMemo(() => findTournament(dashboard, tournamentId), [dashboard, tournamentId]);
  // Mientras llega el perfil nuevo no se redirige con la credencial anterior.
  const cardIsCurrent = !loading && (scopeProfileId == null || dashboard?.profile?.id === scopeProfileId);
  // «Este torneo usa credencial» (Eduardo 2026-10-05): apagado, esta ruta
  // (liga guardada o escrita a mano) explica que no hay credencial.
  const credentialDisabled = cardIsCurrent && card?.credential_enabled === false;
  const credentialUrl = cardIsCurrent && !credentialDisabled ? registrationFileUrl(card?.credential_url) : null;
  // "Volver" del visor regresa al HOME del torneo (Eduardo 2026-08-21): esta
  // ruta redirige al visor cuando hay credencial, así que usarla como
  // destino de regreso formaba un loop visor → credential → visor.
  const backPath = `/player/tournaments/${tournamentId}`;

  if (credentialUrl) {
    return <Navigate to={playerDocumentPath(credentialUrl, "Credencial", backPath)} replace />;
  }

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. La barra usa la
          tarjeta del torneo: no pinta «Credencial» antes de saber. */}
      <PlayerTournamentBottomNav
        tournamentId={tournamentId}
        credentialEnabled={loading ? null : card ? card.credential_enabled !== false : undefined}
      />
      <section className="card player-tournament-credential-card">
        <span className="player-pro-eyebrow">🎫 Credencial</span>
        <h1>Credencial del torneo</h1>
        <PlayerProfileScopeSwitcher scope={scope} tournament />
        {loading ? (
          <p className="muted">Buscando tu credencial…</p>
        ) : error ? (
          <p className="form-error" role="alert">{error}</p>
        ) : card && credentialDisabled ? (
          <>
            <p className="muted">{card.tournament_name ?? "Este torneo"} no usa credencial: no necesitas mostrar ninguna para jugar.</p>
            <Link className="button button--secondary" to={`/player/tournaments/${tournamentId}`}>
              Volver al torneo
            </Link>
          </>
        ) : card ? (
          <>
            <p className="muted">
              Tu credencial de {card.tournament_name} todavía no está generada o publicada.
            </p>
            <Link className="button button--secondary" to={`/player/tournaments/${tournamentId}`}>
              Volver al torneo
            </Link>
          </>
        ) : (
          <p className="muted">No encontramos este torneo en tu cuenta de jugador.</p>
        )}
      </section>
      <TournamentSponsorSlots />
    </>
  );
}
